// firebase/formResponse.service.js
// Centralized Form Submission Service - Multi-User Safe
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    doc,
    onSnapshot,
    writeBatch,
} from 'firebase/firestore';
import { db } from './firebase.config';

const COLLECTION = 'formResponses';

/**
 * Form Response Types
 */
export const FORM_RESPONSE_TYPE = {
    PORTFOLIO: 'portfolio',
    PROPERTY: 'property',
};


/**
 * Required base field IDs that must be present in responses.
 * firstName/lastName are NOT required — leads can be captured with only email or phone.
 * The 'name' field is kept as a fallback for backward compatibility with existing form responses.
 */
const REQUIRED_BASE_FIELDS = ['email'];

/**
 * Optional base fields that are recognized but not required
 */
const OPTIONAL_BASE_FIELDS = ['firstName', 'lastName', 'name', 'phone'];

/**
 * Validate that required fields are present and non-empty
 * @param {Object} responses - Field ID to value mapping
 * @returns {{ valid: boolean, missingFields: string[] }}
 */
const validateRequiredFields = (responses) => {
    const missingFields = [];

    for (const fieldId of REQUIRED_BASE_FIELDS) {
        const value = responses[fieldId];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            missingFields.push(fieldId);
        }
    }

    return {
        valid: missingFields.length === 0,
        missingFields,
    };
};

/**
 * Normalize form responses (trim strings, remove empty optional fields)
 * @param {Object} responses - Raw field ID to value mapping
 * @returns {Object} - Normalized responses
 */
const normalizeResponses = (responses) => {
    const normalized = {};

    for (const [key, value] of Object.entries(responses)) {
        if (Array.isArray(value)) {
            // Multi-select fields: keep arrays as-is (filter empties)
            normalized[key] = value.filter(v => v !== null && v !== undefined && v !== '');
        } else if (typeof value === 'string') {
            const trimmed = value.trim();
            // Keep required and optional base fields even if empty, skip empty custom fields
            const isBaseField = REQUIRED_BASE_FIELDS.includes(key) || OPTIONAL_BASE_FIELDS.includes(key);
            if (trimmed || isBaseField) {
                normalized[key] = trimmed;
            }
        } else if (value !== null && value !== undefined) {
            normalized[key] = value;
        }
    }

    return normalized;
};

/**
 * Build metadata object from browser context
 * @param {Object} options - Optional overrides
 * @returns {Object} - Meta object
 */
const buildMeta = (options = {}) => {
    const meta = {
        pageUrl: options.pageUrl || (typeof window !== 'undefined' ? window.location.href : ''),
        referrer: options.referrer || (typeof document !== 'undefined' ? document.referrer : ''),
        userAgent: options.userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
    };

    // Clean up empty values
    if (!meta.referrer) delete meta.referrer;
    if (!meta.userAgent) delete meta.userAgent;

    return meta;
};

/**
 * Submit a form response for a PORTFOLIO page
 * 
 * @param {Object} params
 * @param {string} params.ownerId - REQUIRED: The portfolio owner's user ID
 * @param {string} params.portfolioId - REQUIRED: The portfolio document ID
 * @param {Object} params.responses - REQUIRED: Field ID to value mapping
 * @param {Object} params.automations - Optional: { addToLeads: boolean }
 * @param {Object} params.meta - Optional: Override metadata
 * @returns {Promise<{ success: boolean, responseId?: string, error?: string }>}
 */
export const submitPortfolioFormResponse = async ({
    ownerId,
    portfolioId,
    responses,
    automations = {},
    meta = {},
}) => {
    // Strict validation: ownerId is REQUIRED
    if (!ownerId) {
        console.error('[formResponse.service] ownerId is required for portfolio form submission');
        return { success: false, error: 'Owner ID is required' };
    }

    if (!portfolioId) {
        console.error('[formResponse.service] portfolioId is required for portfolio form submission');
        return { success: false, error: 'Portfolio ID is required' };
    }

    if (!responses || typeof responses !== 'object') {
        return { success: false, error: 'Responses object is required' };
    }

    // Validate required fields
    const validation = validateRequiredFields(responses);
    if (!validation.valid) {
        return {
            success: false,
            error: `Missing required fields: ${validation.missingFields.join(', ')}`
        };
    }

    try {
        const normalizedResponses = normalizeResponses(responses);
        const responseMeta = buildMeta(meta);

        // Resolve automation flags for storage
        // The Cloud Function will read these to decide whether to create a lead
        const shouldAddToLeads = automations.createLead !== false && automations.addToLeads !== false;
        const tagsToApply = shouldAddToLeads ? (automations.tags || []) : [];

        const formResponseData = {
            ownerId,
            type: FORM_RESPONSE_TYPE.PORTFOLIO,
            portfolioId,
            // formId matches the format used by buildAvailableForms() in formDisplayNames.js
            // so the Cloud Function can match this submission to the correct automation trigger
            formId: `portfolio-${portfolioId}`,
            // propertyId is not set for portfolio forms
            responses: normalizedResponses,
            meta: responseMeta,
            // NOTE: status field intentionally removed — no longer written to new documents
            // Store automation config so the Cloud Function can create leads server-side
            // (public visitors are unauthenticated and cannot write to the leads collection)
            automations: {
                createLead: shouldAddToLeads,
                tags: tagsToApply,
            },
            createdAt: serverTimestamp(),
        };

        // [FORM_TRIGGER_DEBUG] Log form response payload before Firestore write
        console.log('[FORM_TRIGGER_DEBUG] Portfolio form response built:', {
            formId: formResponseData.formId,
            type: formResponseData.type,
            ownerId: formResponseData.ownerId,
            portfolioId: formResponseData.portfolioId,
            addLeadToCRM: shouldAddToLeads,
            tags: tagsToApply,
        });

        // Write to Firestore
        const responsesRef = collection(db, COLLECTION);
        const docRef = await addDoc(responsesRef, formResponseData);

        console.log('[FORM_TRIGGER_DEBUG] Portfolio form response written to Firestore:', {
            responseId: docRef.id,
            formId: formResponseData.formId,
        });
        console.log('[formResponse.service] Portfolio form response created:', docRef.id);
        console.log('[formResponse.service] Automations config stored:', { createLead: shouldAddToLeads, tags: tagsToApply });

        // Lead creation is handled server-side by the onFormResponseCreated Cloud Function
        // which has admin privileges to write to the leads collection

        return { success: true, responseId: docRef.id };
    } catch (error) {
        console.error('[formResponse.service] Error submitting portfolio form:', error);
        return { success: false, error: error.message };
    }
};

/**
 * Submit a form response for a PROPERTY page
 * 
 * @param {Object} params
 * @param {string} params.ownerId - REQUIRED: The property owner's user ID
 * @param {string} params.portfolioId - REQUIRED: The parent portfolio ID
 * @param {string} params.propertyId - REQUIRED: The property document ID
 * @param {Object} params.responses - REQUIRED: Field ID to value mapping
 * @param {Object} params.automations - Optional: { addToLeads: boolean }
 * @param {Object} params.meta - Optional: Override metadata
 * @returns {Promise<{ success: boolean, responseId?: string, error?: string }>}
 */
export const submitPropertyFormResponse = async ({
    ownerId,
    portfolioId,
    propertyId,
    responses,
    automations = {},
    meta = {},
}) => {
    // Strict validation: ownerId is REQUIRED
    if (!ownerId) {
        console.error('[formResponse.service] ownerId is required for property form submission');
        return { success: false, error: 'Owner ID is required' };
    }

    if (!propertyId) {
        console.error('[formResponse.service] propertyId is required for property form submission');
        return { success: false, error: 'Property ID is required' };
    }

    if (!portfolioId) {
        console.error('[formResponse.service] portfolioId is required for property form submission');
        return { success: false, error: 'Portfolio ID is required' };
    }

    if (!responses || typeof responses !== 'object') {
        return { success: false, error: 'Responses object is required' };
    }

    // Validate required fields
    const validation = validateRequiredFields(responses);
    if (!validation.valid) {
        return {
            success: false,
            error: `Missing required fields: ${validation.missingFields.join(', ')}`
        };
    }

    try {
        const normalizedResponses = normalizeResponses(responses);
        const responseMeta = buildMeta(meta);

        // Resolve automation flags for storage
        const shouldAddToLeads = automations.createLead !== false && automations.addToLeads !== false;
        const tagsToApply = shouldAddToLeads ? (automations.tags || []) : [];

        const formResponseData = {
            ownerId,
            type: FORM_RESPONSE_TYPE.PROPERTY,
            portfolioId,
            propertyId,
            // formId matches the format used by buildAvailableForms() in formDisplayNames.js
            // so the Cloud Function can match this submission to the correct automation trigger
            formId: `property-${propertyId}`,
            responses: normalizedResponses,
            meta: responseMeta,
            // NOTE: status field intentionally removed — no longer written to new documents
            // Store automation config so the Cloud Function can create leads server-side
            automations: {
                createLead: shouldAddToLeads,
                tags: tagsToApply,
            },
            createdAt: serverTimestamp(),
        };

        // [FORM_TRIGGER_DEBUG] Log form response payload before Firestore write
        console.log('[FORM_TRIGGER_DEBUG] Property form response built:', {
            formId: formResponseData.formId,
            type: formResponseData.type,
            ownerId: formResponseData.ownerId,
            portfolioId: formResponseData.portfolioId,
            propertyId: formResponseData.propertyId,
            addLeadToCRM: shouldAddToLeads,
            tags: tagsToApply,
        });

        // Write to Firestore
        const responsesRef = collection(db, COLLECTION);
        const docRef = await addDoc(responsesRef, formResponseData);

        console.log('[FORM_TRIGGER_DEBUG] Property form response written to Firestore:', {
            responseId: docRef.id,
            formId: formResponseData.formId,
        });
        console.log('[formResponse.service] Property form response created:', docRef.id);
        console.log('[formResponse.service] Automations config stored:', { createLead: shouldAddToLeads, tags: tagsToApply });

        // Lead creation is handled server-side by the onFormResponseCreated Cloud Function

        return { success: true, responseId: docRef.id };
    } catch (error) {
        console.error('[formResponse.service] Error submitting property form:', error);
        return { success: false, error: error.message };
    }
};

// ============================================================
// READ OPERATIONS (for authenticated owners)
// ============================================================

/**
 * Get all form responses for an owner
 * @param {string} ownerId - The owner's user ID
 * @returns {Promise<Array>} - Array of form response documents
 */
export const getFormResponsesByOwner = async (ownerId) => {
    if (!ownerId) throw new Error('ownerId is required');

    try {
        const responsesRef = collection(db, COLLECTION);
        const q = query(
            responsesRef,
            where('ownerId', '==', ownerId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('[formResponse.service] Error fetching form responses:', error);
        throw error;
    }
};

/**
 * Get form responses for a specific portfolio
 * @param {string} ownerId - The owner's user ID (for security)
 * @param {string} portfolioId - The portfolio document ID
 * @returns {Promise<Array>}
 */
export const getPortfolioFormResponses = async (ownerId, portfolioId) => {
    if (!ownerId || !portfolioId) throw new Error('ownerId and portfolioId are required');

    try {
        const responsesRef = collection(db, COLLECTION);
        const q = query(
            responsesRef,
            where('ownerId', '==', ownerId),
            where('portfolioId', '==', portfolioId),
            where('type', '==', FORM_RESPONSE_TYPE.PORTFOLIO),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('[formResponse.service] Error fetching portfolio responses:', error);
        throw error;
    }
};

/**
 * Get form responses for a specific property
 * @param {string} ownerId - The owner's user ID (for security)
 * @param {string} propertyId - The property document ID
 * @returns {Promise<Array>}
 */
export const getPropertyFormResponses = async (ownerId, propertyId) => {
    if (!ownerId || !propertyId) throw new Error('ownerId and propertyId are required');

    try {
        const responsesRef = collection(db, COLLECTION);
        const q = query(
            responsesRef,
            where('ownerId', '==', ownerId),
            where('propertyId', '==', propertyId),
            orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);

        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
        }));
    } catch (error) {
        console.error('[formResponse.service] Error fetching property responses:', error);
        throw error;
    }
};

/**
 * Subscribe to real-time form responses for an owner
 * @param {string} ownerId - The owner's user ID
 * @param {Function} callback - Called with updated responses array
 * @returns {Function} - Unsubscribe function
 */
export const onFormResponsesChange = (ownerId, callback) => {
    if (!ownerId) {
        console.warn('[onFormResponsesChange] ownerId is empty, returning early');
        callback([]);
        return () => { };
    }

    console.log('[onFormResponsesChange] Setting up listener for ownerId:', ownerId);

    const responsesRef = collection(db, COLLECTION);
    const q = query(
        responsesRef,
        where('ownerId', '==', ownerId),
        orderBy('createdAt', 'desc')
    );

    console.log('[onFormResponsesChange] Query created, subscribing to Firestore');

    const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('[onFormResponsesChange] Snapshot received:', {
            docCount: snapshot.docs.length,
            size: snapshot.size,
            empty: snapshot.empty,
        });

        const responses = snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            console.log('[onFormResponsesChange] Document:', docSnap.id, {
                ownerId: data.ownerId,
                type: data.type,
                hasResponses: !!data.responses,
                responseKeys: Object.keys(data.responses || {}),
            });
            return {
                id: docSnap.id,
                ...data,
            };
        });

        console.log('[onFormResponsesChange] Callback invoked with', responses.length, 'responses');
        callback(responses);
    }, (error) => {
        console.error('[onFormResponsesChange] Firestore error:', error);
        callback([]);
    });

    return unsubscribe;
};

/**
 * Get a single form response by ID (with ownership verification)
 * @param {string} ownerId - The owner's user ID
 * @param {string} responseId - The response document ID
 * @returns {Promise<Object|null>}
 */
export const getFormResponseById = async (ownerId, responseId) => {
    if (!ownerId || !responseId) return null;

    try {
        const responseRef = doc(db, COLLECTION, responseId);
        const docSnap = await getDoc(responseRef);

        if (!docSnap.exists()) return null;

        const data = docSnap.data();

        // Verify ownership
        if (data.ownerId !== ownerId) {
            console.warn('[formResponse.service] Unauthorized access attempt');
            return null;
        }

        return {
            id: docSnap.id,
            ...data,
        };
    } catch (error) {
        console.error('[formResponse.service] Error fetching response:', error);
        return null;
    }
};

/**
 * Batch delete form responses by IDs
 * Firestore writeBatch supports max 500 operations per batch.
 * This function auto-splits into multiple batches if needed.
 *
 * @param {string[]} responseIds - Array of response document IDs to delete
 * @returns {Promise<{ success: boolean, deletedCount: number, error?: string }>}
 */
export const deleteFormResponses = async (responseIds) => {
    if (!responseIds || responseIds.length === 0) {
        return { success: true, deletedCount: 0 };
    }

    const BATCH_LIMIT = 500;
    let totalDeleted = 0;

    try {
        // Split into chunks of 500
        for (let i = 0; i < responseIds.length; i += BATCH_LIMIT) {
            const chunk = responseIds.slice(i, i + BATCH_LIMIT);
            const batch = writeBatch(db);

            for (const id of chunk) {
                const docRef = doc(db, COLLECTION, id);
                batch.delete(docRef);
            }

            await batch.commit();
            totalDeleted += chunk.length;
        }

        console.log(`[formResponse.service] Batch deleted ${totalDeleted} responses`);
        return { success: true, deletedCount: totalDeleted };
    } catch (error) {
        console.error('[formResponse.service] Batch delete error:', error);
        return {
            success: false,
            deletedCount: totalDeleted,
            error: error.message || 'Failed to delete responses',
        };
    }
};
